import * as React from "react";
import {
  Box,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CircleIcon from "@mui/icons-material/Circle";
import equipbg from "../assets/equipments.jpg";
import RevealCard from "./RevealCard";
import steelcat from "../assets/equipments/steelcat.jpg";
import sabercat from "../assets/equipments/sabercat.jpg";
import bianchi from "../assets/equipments/bianchi.jpg";
import battlecat from "../assets/equipments/battlecat.jpg";
import tomcat from "../assets/equipments/tomcat.jpg";
import a01 from "../assets/equipments/a01.jpg";

const eyeItems = [
  "Oakley FLAK 2.0",
  "Oakley SI Ballistic M Frame Alpha",
  "OakleySpeed Jacket ",
];
const earItems = ['AXIL "Ghost Stryke"', "ISOtunes Sport"];
const dotItems = ["C-More RTS3", "Ghost Optic SO-10"];

export default function EquipmentsSection(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const pistolsItems =
    (t("equipments.pistolsItems", { returnObjects: true }) as any[]) || [];
  const pistolsImages = [steelcat, sabercat, bianchi, battlecat, tomcat, a01];
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${equipbg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 75%",
          opacity: 0.3,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="equipments"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              {t("equipments.title")}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="-5.0 -10.0 110.0 135.0"
              >
                <path d="m22.188 28.297c-0.78125-0.39062-1.7031-0.078125-2.0938 0.70312l-1.5625 3.125c-0.39062 0.76562-0.078125 1.7031 0.70312 2.0938 0.21875 0.10938 0.46875 0.17188 0.70312 0.17188 0.57812 0 1.125-0.3125 1.4062-0.85938l1.5625-3.125c0.39062-0.76562 0.078125-1.7031-0.70312-2.0938z" />
                <path d="m30 28.297c-0.76562-0.39062-1.7031-0.078125-2.0938 0.70312l-1.5625 3.125c-0.39062 0.76562-0.078125 1.7031 0.70312 2.0938 0.21875 0.10938 0.46875 0.17188 0.70312 0.17188 0.57812 0 1.125-0.3125 1.4062-0.85938l1.5625-3.125c0.39062-0.76562 0.078125-1.7031-0.70312-2.0938z" />
                <path d="m37.812 28.297c-0.76562-0.39062-1.7031-0.078125-2.0938 0.70312l-1.5625 3.125c-0.39062 0.76562-0.078125 1.7031 0.70312 2.0938 0.21875 0.10938 0.46875 0.17188 0.70312 0.17188 0.57812 0 1.125-0.3125 1.4062-0.85938l1.5625-3.125c0.39062-0.76562 0.078125-1.7031-0.70312-2.0938z" />
                <path d="m90.234 26.562h-3.125v-1.5625c0-0.85938-0.70312-1.5625-1.5625-1.5625h-3.125v-1.5625c0-0.85938-0.70312-1.5625-1.5625-1.5625s-1.5625 0.70312-1.5625 1.5625v1.5625h-64.062c-2.5938 0-4.6875 2.1094-4.6875 4.6875v9.5938c0 2.2344 1.3438 4.2656 3.5156 5.3125 0.40625 0.20312 0.76562 0.53125 1.0781 0.96875l-6.8125 29.953c-0.32812 1.4062 0.015625 2.8438 0.90625 3.9688s2.2344 1.7656 3.6562 1.7656h12.344c2.2031 0 4.0781-1.5 4.5781-3.6406l4.1406-18.234h9.4062c7.2188 0 13.188-5.4844 13.969-12.5h20.406c2.5781 0 4.6875-2.1094 4.6875-4.6875v-1.5625h3.125c0.85938 0 1.5625-0.70312 1.5625-1.5625v-1.5625h3.125c0.85938 0 1.5625-0.70312 1.5625-1.5625v-6.25c0-0.85938-0.70312-1.5625-1.5625-1.5625zm-73.422 45.312 4.9688-21.875h5.9375l-4.9688 21.875zm26.547-17.188h-8.6875l1.2969-5.7344c0.34375-1.5 1.3594-2.6875 2.7031-3.2656v1.1875c0 2.5781 2.0938 4.6875 4.6875 4.6875 0.85938 0 1.5625-0.70312 1.5625-1.5625s-0.70312-1.5625-1.5625-1.5625-1.5625-0.70312-1.5625-1.5625v-1.5625h12.375c-0.76562 5.2969-5.3125 9.375-10.812 9.375zm37.5-18.75h-67.188v-7.8125c0-0.85938 0.70312-1.5625 1.5625-1.5625h68.75v9.3438h-3.125zm7.8125-3.125h-1.5625v-3.125h1.5625z" />
              </SvgIcon>
              {t("equipments.pistolsTitle")}
            </Typography>
            <Grid container spacing={4} sx={{ justifyContent: "center" }}>
              {pistolsItems.map((item, i) => (
                <Grid size={{ xs: 12, md: 6 }}>
                  <RevealCard
                    title={item.title}
                    subtitle={item.subtitle}
                    image={pistolsImages[i]}
                    description={item.description}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="0 0 1000 1250"
              >
                <path d="M900,839.66H100a16,16,0,0,1-16-16v-223a16,16,0,0,1,16-16H900a16,16,0,0,1,16,16v223A16,16,0,0,1,900,839.66Zm-784-32H884v-191H116Z" />
                <path d="M852.42,792.08H147.58a16,16,0,0,1-16-16V648.24a16,16,0,0,1,16-16H852.42a16,16,0,0,1,16,16V776.08A16,16,0,0,1,852.42,792.08Zm-688.84-32H836.42V664.24H163.58Z" />
                <path d="M873.9,616.67a16,16,0,0,1-15.65-12.77L779.17,221.47a36.67,36.67,0,0,0-35.78-29.13H256.61a36.67,36.67,0,0,0-35.78,29.13L141.75,603.9a16,16,0,0,1-31.34-6.48L189.49,215a68.81,68.81,0,0,1,67.12-54.65H743.39A68.79,68.79,0,0,1,810.5,215l79.09,382.43a16,16,0,0,1-12.43,18.91A15.77,15.77,0,0,1,873.9,616.67Z" />
                <path d="M500,538.19c-82.54,0-149.69-67.15-149.69-149.69S417.46,238.81,500,238.81,649.69,306,649.69,388.5,582.54,538.19,500,538.19Zm0-267.38A117.69,117.69,0,1,0,617.69,388.5,117.82,117.82,0,0,0,500,270.81Z" />
                <path d="M500,473.13a84.63,84.63,0,1,1,84.63-84.63A84.72,84.72,0,0,1,500,473.13Zm0-137.26a52.63,52.63,0,1,0,52.63,52.63A52.69,52.69,0,0,0,500,335.87Z" />
                <path d="M500,364.68a16,16,0,0,1-16-16V232.81a16,16,0,1,1,32,0V348.68A16,16,0,0,1,500,364.68Z" />
                <path d="M655.69,404.5H539.82a16,16,0,0,1,0-32H655.69a16,16,0,0,1,0,32Z" />
                <path d="M500,560.19a16,16,0,0,1-16-16V428.32a16,16,0,1,1,32,0V544.19A16,16,0,0,1,500,560.19Z" />
                <path d="M460.18,404.5H344.31a16,16,0,1,1,0-32H460.18a16,16,0,1,1,0,32Z" />
              </SvgIcon>
              {t("equipments.dotsTitle")}
            </Typography>
            <List dense>
              {dotItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("equipments.dotsEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="0 0 100 125"
              >
                <path d="M55.44,37.66c0.1-0.45-0.02-0.93-0.31-1.3c-0.32-0.4-0.81-0.63-1.29-0.63l0,0l-1.41-0.12c-1.71-0.15-3.45-0.15-5.16,0   l-1.38,0.12c-0.51,0-0.99,0.23-1.31,0.62c-0.3,0.37-0.42,0.85-0.33,1.31c0.04,0.22,0.14,0.47,0.33,0.83l1.04,1.32   c0.1,0.15,0.98,1.47,1.95,1.47h4.56c0.97,0,1.85-1.32,1.92-1.44l1.09-1.4C55.29,38.17,55.39,37.92,55.44,37.66z M54.7,37.51   c-0.04,0.18-0.11,0.36-0.19,0.51l-1.08,1.38c-0.24,0.37-0.87,1.13-1.31,1.13h-4.56c-0.4,0-1.01-0.66-1.33-1.16l-1.02-1.29   c-0.12-0.23-0.2-0.42-0.23-0.57c-0.05-0.24,0.01-0.48,0.17-0.68c0.18-0.22,0.45-0.35,0.76-0.35l1.41-0.12   c0.84-0.07,1.68-0.11,2.52-0.11s1.68,0.04,2.51,0.11l1.44,0.12c0.28,0,0.56,0.13,0.74,0.35C54.7,37.03,54.76,37.27,54.7,37.51   L54.7,37.51z" />
                <path d="M41.48,37.51c-1.66-1.03-3.62-1.32-5.49-1.49c-1.11-0.09-2.85-0.18-4.78-0.13c-0.69-0.03-16.97-0.59-18.57,7.26   c-1.57,7.69-1.21,12.86,1.13,16.27c2.69,3.92,5.16,4.4,9.28,4.91l0.26,0.03c1.35,0.17,2.85,0.36,4.42,0.36   c3.69,0,7.75-1.06,11.15-5.9l0,0c4.84-6.92,6.88-12.47,6.06-16.48C44.53,40.31,43.36,38.68,41.48,37.51z" />
                <path d="M68.81,35.89c-1.95-0.05-3.69,0.04-4.8,0.13c-1.87,0.16-3.83,0.45-5.49,1.49c-1.88,1.17-3.05,2.8-3.46,4.84   c-0.82,4.01,1.22,9.55,6.07,16.48c3.4,4.85,7.46,5.9,11.15,5.9c1.57,0,3.07-0.19,4.42-0.36l0.26-0.03   c4.12-0.51,6.59-0.99,9.28-4.91l0,0c2.34-3.41,2.7-8.58,1.13-16.27C85.75,35.3,69.48,35.86,68.81,35.89z" />
                <path d="M92.71,40.75L92.71,40.75c-0.47-0.81-1.19-1.17-2.02-1.53c-0.49-0.2-0.94-0.39-1.34-0.58c-1.17-0.54-2.05-1.05-2.8-1.61   c-2.66-2-6.66-2.42-10.53-2.83c-5.58-0.58-25.82-0.94-26.04-0.94c-0.2,0-20.44,0.36-26.02,0.94c-3.87,0.41-7.86,0.83-10.53,2.83   c-0.75,0.56-1.64,1.07-2.8,1.61c-0.41,0.19-0.85,0.38-1.35,0.59c-0.82,0.35-1.53,0.71-2.01,1.53c-0.3,0.53-0.46,2.09-0.46,2.64   c0,1.53,0.33,1.9,0.88,2.37c0.09,0.08,0.18,0.16,0.28,0.26c0.73,0.71,1.06,1.36,1,3.14c-0.06,1.91,0.44,4.67,0.46,4.77   c0.01,0.11,1.43,10.6,10.4,12.01c2.93,0.46,5.58,0.81,8.01,0.81c5.36,0,9.64-1.7,13.39-7.64c0.03-0.04,2.6-4.04,4.81-9.11   c0.5-1.15,0.98-2.37,1.44-3.72c0.29-0.86,1.12-2.86,2.49-2.86s2.2,2,2.5,2.86c0.46,1.35,0.94,2.56,1.44,3.72   c2.21,5.08,4.78,9.07,4.81,9.11c5.45,8.63,12.01,8.31,21.41,6.83c8.97-1.42,10.39-11.91,10.4-11.99c0.02-0.12,0.53-2.88,0.47-4.79   c-0.07-1.78,0.27-2.43,1-3.14c0.1-0.1,0.2-0.18,0.28-0.26c0.54-0.47,0.88-0.84,0.88-2.37C93.17,42.84,93.02,41.28,92.71,40.75z    M91.81,45.19c-0.1,0.09-0.21,0.18-0.32,0.29c-0.85,0.83-1.3,1.67-1.22,3.7c0.06,1.83-0.45,4.6-0.46,4.65   c-0.01,0.1-1.35,10.04-9.78,11.37c-9.37,1.48-15.44,1.77-20.66-6.49c-0.03-0.04-2.57-3.98-4.75-9.01c-0.5-1.13-0.96-2.33-1.42-3.66   c-0.43-1.26-1.41-3.36-3.21-3.36s-2.78,2.11-3.2,3.36c-0.46,1.33-0.92,2.53-1.42,3.66c-2.18,5.02-4.72,8.97-4.75,9.01   c-5.22,8.26-11.28,7.96-20.65,6.49c-8.41-1.33-9.76-11.27-9.78-11.39c-0.01-0.03-0.51-2.8-0.45-4.63c0.08-2.04-0.37-2.87-1.22-3.7   c-0.11-0.11-0.22-0.2-0.32-0.29c-0.41-0.36-0.62-0.53-0.62-1.8c0-0.6,0.18-1.95,0.36-2.26c0.36-0.62,0.91-0.89,1.64-1.21   c0.51-0.2,0.96-0.41,1.38-0.6c1.22-0.56,2.15-1.1,2.94-1.69c2.5-1.88,6.39-2.29,10.15-2.69C29.6,34.36,49.8,34.01,49.99,34   c0.2,0,20.41,0.36,25.96,0.94c3.76,0.4,7.66,0.81,10.15,2.69c0.79,0.59,1.72,1.13,2.94,1.69c0.42,0.19,0.87,0.4,1.37,0.59   c0.74,0.32,1.29,0.6,1.65,1.21c0.18,0.32,0.36,1.66,0.36,2.26C92.42,44.66,92.22,44.84,91.81,45.19z" />
              </SvgIcon>
              {t("equipments.eyesTitle")}
            </Typography>
            <List dense>
              {eyeItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("equipments.eyesEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="-5.0 -10.0 110.0 135.0"
              >
                <path
                  d="m30.273 43.406c-2.3242 0-4.5117-0.875-6.1562-2.4648-0.15234-0.14844-0.23828-0.35156-0.23828-0.5625v-11.719c0-0.21094 0.089844-0.41406 0.23828-0.5625 1.6445-1.5898 3.832-2.4648 6.1562-2.4648 2.3594 0 4.5781 0.92188 6.25 2.5977 1.6719 1.6758 2.5859 3.9102 2.5859 6.2891 0 2.3789-0.91797 4.6094-2.5859 6.2891-1.6719 1.6758-3.8906 2.5977-6.25 2.5977z"
                  fill-rule="evenodd"
                />
                <path
                  d="m35.449 61.77c-2.0234 0-3.6641-1.6406-3.6641-3.6641v-13.246c2.207-0.32031 4.2461-1.3438 5.8438-2.9492 0.57031-0.57422 1.0664-1.1992 1.4805-1.8711v18.062c0 0.98828-0.375 1.9102-1.0625 2.5977s-1.6133 1.0625-2.5977 1.0625z"
                  fill-rule="evenodd"
                />
                <path
                  d="m22.312 41.113h-3.3203c-3.5781 0-6.4961-2.9141-6.4961-6.4961 0-1.7227 0.67578-3.3398 1.8945-4.5547 1.2266-1.2148 2.8555-1.8906 4.6016-1.8906h3.3203z"
                  fill-rule="evenodd"
                />
                <path
                  d="m50.148 82.082c-0.85938 0-1.5625-0.70312-1.5625-1.5625v-6.0078c0-1.5-1.1719-2.9805-2.7344-2.9805h-5.8594c-3.3477 0-6.1016-2.6953-6.1016-6.0547v-4.4922c0-0.85938 0.70312-1.5625 1.5625-1.5625s1.5625 0.70312 1.5625 1.5625v4.4922c0 1.6367 1.3555 2.9297 2.9805 2.9297h5.8594c3.2773 0 5.8594 2.8828 5.8594 6.1016v6.0078c0 0.85938-0.70312 1.5625-1.5625 1.5625z"
                  fill-rule="evenodd"
                />
                <path
                  d="m69.727 35.695c-2.3594 0-4.5781-0.92187-6.25-2.5977-1.6719-1.6758-2.5859-3.9102-2.5859-6.2891 0-2.3594 0.91797-4.5859 2.5859-6.2695 1.6758-1.6914 3.8906-2.6172 6.25-2.6172 2.2656 0 4.4531 0.875 6.1484 2.457 0.15625 0.14844 0.25 0.35547 0.25 0.57031v11.719c0 0.21094-0.089844 0.41406-0.23828 0.5625-1.6445 1.5938-3.832 2.4648-6.1562 2.4648z"
                  fill-rule="evenodd"
                />
                <path
                  d="m64.551 54.055c-0.98828 0-1.9102-0.375-2.5977-1.0625s-1.0625-1.6133-1.0625-2.5977v-18.062c0.41797 0.67188 0.91016 1.2969 1.4805 1.8711 1.6016 1.6055 3.6367 2.625 5.8438 2.9492v13.246c0 2.0234-1.6406 3.6641-3.6641 3.6641z"
                  fill-rule="evenodd"
                />
                <path
                  d="m81.008 33.398h-3.3203v-12.941h3.3203c1.7383 0 3.375 0.66797 4.6016 1.8906 1.2227 1.2148 1.8945 2.8359 1.8945 4.5547 0 3.5781-2.9141 6.4961-6.4961 6.4961z"
                  fill-rule="evenodd"
                />
                <path
                  d="m49.852 74.363c-0.85937 0-1.5625-0.70312-1.5625-1.5625v-6.0078c0-1.6016 0.61719-3.1211 1.7305-4.2695 1.0781-1.1133 2.5742-1.7852 4.1289-1.7852h5.8594c1.6445 0 2.9805-1.3359 2.9805-2.9805v-4.4922c0-0.85938 0.70312-1.5625 1.5625-1.5625s1.5625 0.70312 1.5625 1.5625v4.4922c0 1.625-0.64453 3.1602-1.7969 4.3086-1.1523 1.1445-2.6797 1.793-4.3047 1.793h-5.8594c-1.5469 0-2.7344 1.4336-2.7344 2.9297v6.0078c0 0.85938-0.70312 1.5625-1.5625 1.5625z"
                  fill-rule="evenodd"
                />
              </SvgIcon>
              {t("equipments.earsTitle")}
            </Typography>
            <List dense>
              {earItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="-5.0 -10.0 110.0 135.0"
              >
                <path d="m80.301 15.898c-0.60156-0.80078-1.3008-1.3008-2-5.3008s-16.5 0.80078-21.301 2.8984c-4.8984 2.1016-10.602-1.6992-11.398-3.6016-0.80078-1.8984-2.5-4.3008-4.1016-5-1.8008-0.79297-14.5 9.8047-15 10.605s0 7.1992 0 7.1992 0.19922 1.6992 0.60156 4.1992c0.19922 0.30078 0.39844 0.69922 0.60156 1 1.5 2.3008 3.3984 4.6992 5.6016 6.3008 0.5 0.39844 1 0.69922 1.5 1 5.3984 3.8008 10.898 6.6016 15.898 8.8008 0.10156-2 0.10156-4.3984 1.3984-4.6992 0.69922-0.19922 1.8008-0.19922 3-0.30078 0.89844 0.30078 1.8008 0.80078 2.1016 1.6992 0.30078 1.1016 0.30078 4.1992 0.30078 6 1.1016 0.39844 2.1016 0.69922 3.1016 1.1016-0.19922-1.8008-0.5-4.3984-0.5-5.6016 0-2 2.3008-3.6016 1.5-4.3008 0 0-0.10156-0.10156-0.19922-0.10156 1.6016-1.1016 8.8008-1.8008 13.602-2.5 5.1992-0.69922 3.6016 0.10156 3.6992-0.80078 0.10156-0.89844 1.3008 0.39844 1.8984 0.19922 0.79297-0.19531 0.29297-17.996-0.30469-18.797zm-4.1016 16.602c-2.3008 0.60156-22.898-0.19922-24.801-2.8984-2-2.7031-4-14.602-4-14.602s11.602 3.6992 15.398 1.8008c3.8008-1.8008 10.699-6.5 12.801-1.1016 2.2031 5.4023 2.9023 16.199 0.60156 16.801z" />
                <path d="m70.398 59.5c-0.5 0.5-1.1016 0.69922-1.8008 0.69922h-0.39844c-3.3984-0.5-20.301-6.1992-31.098-13.199-9.3008-6-14.699-12.602-16.5-17.199-0.60156 11-3 55.699-0.60156 59.199 2.8008 3.8984 30 12.199 34.199-0.89844 3.1992-10 15.5-11.602 16.199-18 0.5-4.1016 0.60156-8.1016 0.5-11.301-0.097657 0.30078-0.29688 0.5-0.5 0.69922z" />
                <path d="m37.801 46c10.598 6.8984 27.199 12.5 30.5 12.898 0.5 0.10156 0.89844-0.10156 1.1992-0.39844 0.80078-0.80078 1.1016-2.6016 1.1016-4.1016v-2.1992c-0.10156-0.10156-0.19922-0.10156-0.39844-0.19922-0.60156-0.30078-1.6992-0.69922-3.1016-1.1016-0.5-0.19922-1-0.30078-1.6016-0.5-1.3984-0.39844-3-0.89844-4.8984-1.5-1.1016-0.30078-2.1992-0.69922-3.3008-1.1016-2.1016-0.69922-4.3984-1.6016-6.8984-2.6016-3.3984-1.3984-7-3.1992-10.699-5.3008-2.3984-1.3984-4.8984-3-7.3008-4.8008-1.8984-1.3984-3.6016-3.3008-5-5.1992-1.5-2-2.6992-4.1992-3.5-5.8984-0.5-1.1016-1-2-1.3008-2.6992-0.60156 1-1.3984 2.6992-1.6016 4.5 0 0.5-0.10156 0.89844 0 1.3984 0.60156 3.5039 5.3008 11.305 16.801 18.805z" />
              </SvgIcon>
              {t("equipments.holsterTitle")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CircleIcon sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    i18n.language === "en"
                      ? 'Japanese N-Factory "Hayabusa Super Draw Model"'
                      : '日本製 N-Factory "Hayabusa Super Draw Model"'
                  }
                  sx={{ color: "text.primary" }}
                />
              </ListItem>
            </List>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SvgIcon
                fontSize="large"
                sx={{ verticalAlign: "middle", mr: 1 }}
                viewBox="-5.0 -10.0 110.0 135.0"
              >
                <path d="m95.945 56.016c-0.019531-0.050781-0.039062-0.10156-0.070312-0.14062-0.011719-0.011719-0.011719-0.019531-0.019531-0.03125-0.058594-0.089844-0.12109-0.17188-0.17188-0.25-0.21094-0.28906-0.42188-0.57031-0.62109-0.82812-2.7891-3.4883-6.0703-4.2617-15.871-6.5703l-2.9492-0.69141c-13.121-3.1211-27.66-9.5586-32.422-14.371-2.6016-2.6211-5.9805-5.168-7.6094-4.9805-0.46875 0.050781-0.71875 0.30859-0.83984 0.51172-0.16016 0.26172-0.33984 0.57031-0.53125 0.91016-1.5391 2.7109-4.3984 7.75-10.211 7.75-3.4492 0-5.3203-1.75-6.9805-3.2812-1.5117-1.3984-2.9297-2.7305-5.1406-2.1914-4.6211 1.1289-9.2109 7.6211-8.3789 19.199 0.80859 11.41 7.1914 13.891 7.9805 14.16 0.019531 0.011718 0.039063 0.019531 0.070313 0.019531 0.039062 0.019531 0.070312 0.03125 0.10937 0.03125 0.19141 0.011719 0.37891 0.03125 0.55859 0.039062l2.3086 6.5508h3.8086l2.0781-5.9219c1.25 0.10156 2.3984 0.19922 3.4609 0.28906l1.9805 5.6289h3.8203l1.75-4.9609c4.6406 0.39844 7.9297 0.60156 14.078 0.60156h2.1602c1.7812-0.011719 3.8398-0.050781 6.0898-0.12891l1.5781 4.4883h3.8086l1.7109-4.8398c1.3086-0.089843 2.6406-0.19922 3.9883-0.32031l1.8203 5.1602h3.8203l2.1406-6.0703c1.3789-0.19141 2.7383-0.42188 4.0781-0.67188l1.7109 4.8281h3.8203l2.3789-6.7383c0.76172-0.23047 1.5-0.48047 2.2109-0.73828 5.2109-1.9102 8.0586-3.9609 8.4609-6.1211 0.0625-0.10156 0.0625-0.22266 0.035156-0.32031zm-67.461-1.3516c-10.75 7.4102-9.0195-8.25-9.0195-8.25s0.51953 4.25 3.25 4.8086c4.0117 0.82812 18.91-9.0117 19.359-9.3008-0.15234 0.20312-5.1992 6.9531-13.59 12.742z" />
              </SvgIcon>
              {t("equipments.shoesTitle")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CircleIcon sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    i18n.language === "en"
                      ? "NIKE Alpha Shark series (Wide Model)"
                      : "NIKE Alpha Shark シリーズ（ワイドモデル）"
                  }
                  sx={{ color: "text.primary" }}
                />
              </ListItem>
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("equipments.shoesEnd")}
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
